import * as React from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { X } from 'lucide-react';
import { cn } from '../lib/utils';

const ToastProvider = ToastPrimitive.Provider;

const ToastViewport = React.forwardRef<
	React.ElementRef<typeof ToastPrimitive.Viewport>,
	React.ComponentPropsWithoutRef<typeof ToastPrimitive.Viewport>
>(({ className, ...props }, ref) => (
	<ToastPrimitive.Viewport
		ref={ref}
		className={cn('fixed bottom-6 left-1/2 z-[200] flex max-h-screen w-full max-w-[420px] -translate-x-1/2 flex-col gap-2 p-2 outline-none', className)}
		{...props}
	/>
));
ToastViewport.displayName = ToastPrimitive.Viewport.displayName;

const Toast = React.forwardRef<
	React.ElementRef<typeof ToastPrimitive.Root>,
	React.ComponentPropsWithoutRef<typeof ToastPrimitive.Root> & { tone?: 'default' | 'success' | 'destructive' }
>(({ className, tone = 'success', ...props }, ref) => {
	const toneClass =
		tone === 'success'
			? 'bg-[hsl(var(--success))] text-white'
			: tone === 'destructive'
				? 'bg-destructive text-white'
				: 'bg-slate-900 text-white';
	return (
		<ToastPrimitive.Root
			ref={ref}
			className={cn(
				'flex items-center justify-between gap-3 rounded-lg px-4 py-2.5 text-sm font-medium shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=open]:slide-in-from-bottom-full',
				toneClass,
				className
			)}
			{...props}
		/>
	);
});
Toast.displayName = ToastPrimitive.Root.displayName;

const ToastTitle = React.forwardRef<
	React.ElementRef<typeof ToastPrimitive.Title>,
	React.ComponentPropsWithoutRef<typeof ToastPrimitive.Title>
>(({ className, ...props }, ref) => (
	<ToastPrimitive.Title ref={ref} className={cn('text-sm font-medium', className)} {...props} />
));
ToastTitle.displayName = ToastPrimitive.Title.displayName;

const ToastDescription = React.forwardRef<
	React.ElementRef<typeof ToastPrimitive.Description>,
	React.ComponentPropsWithoutRef<typeof ToastPrimitive.Description>
>(({ className, ...props }, ref) => (
	<ToastPrimitive.Description ref={ref} className={cn('text-xs opacity-90', className)} {...props} />
));
ToastDescription.displayName = ToastPrimitive.Description.displayName;

const ToastClose = React.forwardRef<
	React.ElementRef<typeof ToastPrimitive.Close>,
	React.ComponentPropsWithoutRef<typeof ToastPrimitive.Close>
>(({ className, ...props }, ref) => (
	<ToastPrimitive.Close ref={ref} className={cn('opacity-70 hover:opacity-100', className)} {...props}>
		<X className="h-3.5 w-3.5" />
	</ToastPrimitive.Close>
));
ToastClose.displayName = ToastPrimitive.Close.displayName;

export type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;
export { Toast, ToastProvider, ToastViewport, ToastTitle, ToastDescription, ToastClose };
